import { useEffect } from 'react';
import { X, Trash2, Plus, Minus, ImageOff, ArrowRight } from 'lucide-react';
import { useStorefront } from './useStorefront';
import { getVariantDisplay } from './variantLabels';

/**
 * Slide-out mini-cart drawer.
 *
 * Renders the local cart contents, lets users adjust quantities or remove
 * items, then bounces them to the Shopify multi-variant cart permalink
 * for secure checkout. Local state only — Shopify owns payments.
 */
const MiniCart = ({ open, onClose }) => {
  const { cart, cartCount, cartTotal, updateQty, removeItem, clearCart, checkoutUrl } = useStorefront();

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`sf-cart-backdrop ${open ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
        data-testid="sf-cart-backdrop"
      />
      <aside
        className={`sf-cart-drawer ${open ? 'open' : ''}`}
        role="dialog"
        aria-label="Shopping cart"
        aria-modal="true"
        data-testid="sf-cart-drawer"
      >
        <header className="sf-cart-header">
          <div>
            <p className="sf-label">Your cart</p>
            <h2 className="text-2xl font-bold mt-1 tracking-tight" data-testid="sf-cart-title">
              {cartCount} {cartCount === 1 ? 'item' : 'items'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close cart"
            className="sf-cart-close"
            data-testid="sf-cart-close"
          >
            <X size={22} />
          </button>
        </header>

        <div className="sf-cart-items" data-testid="sf-cart-items">
          {cart.length === 0 ? (
            <div className="sf-cart-empty">
              <p className="sf-label text-zinc-500">Empty</p>
              <p className="mt-2 text-zinc-400 text-sm">Nothing in the warp pipe yet. Pick something rad from the catalog.</p>
            </div>
          ) : (
            cart.map((c) => {
              const { label: variantLabel } = getVariantDisplay(c.variantTitle);
              const showVariant = variantLabel && variantLabel !== 'Default Title';
              return (
              <div key={c.variantId} className="sf-cart-row" data-testid={`sf-cart-row-${c.variantId}`}>
                <div className="sf-cart-thumb">
                  {c.image ? (
                    <img src={c.image} alt={c.title} />
                  ) : (
                    <div className="sf-cart-thumb-empty"><ImageOff size={20} /></div>
                  )}
                </div>
                <div className="sf-cart-info">
                  <p className="sf-cart-item-title" data-testid={`sf-cart-title-${c.variantId}`}>{c.title}</p>
                  {showVariant && (
                    <p className="sf-cart-item-variant">{variantLabel}</p>
                  )}
                  <p className="sf-cart-item-price">${Number(c.price || 0).toFixed(2)}</p>
                  <div className="sf-cart-qty" data-testid={`sf-cart-qty-${c.variantId}`}>
                    <button
                      type="button"
                      onClick={() => updateQty(c.variantId, c.quantity - 1)}
                      aria-label="Decrease quantity"
                      data-testid={`sf-cart-decr-${c.variantId}`}
                    ><Minus size={14} /></button>
                    <span data-testid={`sf-cart-qty-value-${c.variantId}`}>{c.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQty(c.variantId, c.quantity + 1)}
                      disabled={c.stock != null && c.quantity >= Number(c.stock)}
                      aria-label="Increase quantity"
                      data-testid={`sf-cart-incr-${c.variantId}`}
                    ><Plus size={14} /></button>
                  </div>
                  {c.stock != null && c.quantity >= Number(c.stock) && (
                    <p className="sf-cart-cap-note" data-testid={`sf-cart-cap-${c.variantId}`}>
                      Max stock reached
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(c.variantId)}
                  aria-label="Remove item"
                  className="sf-cart-remove"
                  data-testid={`sf-cart-remove-${c.variantId}`}
                ><Trash2 size={16} /></button>
              </div>
              );
            })
          )}
        </div>

        {cart.length > 0 && (
          <footer className="sf-cart-footer">
            <div className="sf-cart-subtotal">
              <span className="sf-label">Subtotal</span>
              <span className="sf-cart-total" data-testid="sf-cart-subtotal">${cartTotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-zinc-500 mb-3">Shipping &amp; taxes calculated at Shopify checkout.</p>
            <a
              href={checkoutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="sf-btn-primary w-full justify-center"
              data-testid="sf-cart-checkout"
            >
              Checkout on Shopify <ArrowRight size={16} />
            </a>
            <button
              type="button"
              onClick={clearCart}
              className="sf-cart-clear"
              data-testid="sf-cart-clear"
            >Clear cart</button>
          </footer>
        )}
      </aside>
    </>
  );
};

export default MiniCart;
